import { Cross2Icon, EyeNoneIcon } from '@radix-ui/react-icons';
import { Card, Flex, Grid, Skeleton, Text } from '@radix-ui/themes';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { PitcherHandBadge } from 'components/common/pitcher-hand-badge';
import usePitchPerformanceStore from 'components/sections/admin-portal/machines/dialogs/pitch-performance/store/use-pitch-performance-store';
import { GameDataPlayerAvatar } from 'components/sections/game-data/players/avatar';
import { GameDataTeamLogo } from 'components/sections/game-data/team-logo';
import { t } from 'i18next';
import { isAppearanceDark } from 'index';
import { IOptionExt } from 'interfaces/forms/option-ext';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

export const StatcastSearchDialog = () => {
  const {
    loading,
    dialog,
    firstGroups,
    season,
    pitchers,
    pitcherPk,
    seasonOptions,
    pitchGroups,
    setSeason,
    setPitcherPk,
    fetchSeasons,
    fetchPitchers,
    fetchPitches,
    setStatsDialog,
  } = usePitchPerformanceStore(
    useShallow((state) => ({
      loading: state.statsLoading,
      firstGroups: state.firstGroups,

      seasonOptions: state.statsSeasonOptions,

      season: state.statsSeason,
      setSeason: state.setStatsSeason,
      fetchSeasons: state.fetchStatsSeasons,

      pitchers: state.statsPitchers,
      pitcherPk: state.statsPitcherPk,
      setPitcherPk: state.setStatsPitcherPk,

      fetchPitchers: state.fetchStatsPitchers,
      fetchPitches: state.fetchStatsPitches,

      pitchGroups: state.statsPitchGroups,

      dialog: state.statsDialog,
      setStatsDialog: state.setStatsDialog,
    }))
  );

  useEffect(() => {
    if (seasonOptions.length === 0) {
      // load seasons 1x
      fetchSeasons();
      return;
    }

    if (season) {
      // don't manipulate season if already set from before
      return;
    }
    // select the first season (should be the latest one) by default
    setSeason(parseInt(seasonOptions[0].value));
  }, [seasonOptions]);

  useEffect(() => {
    if (!season) {
      return;
    }

    fetchPitchers();
  }, [season]);

  useEffect(() => {
    fetchPitches(firstGroups, false);
  }, [pitcherPk]);

  const pitcherOptions = useMemo(() => {
    const NO_TEAM_GROUP = 'No Active Roster';

    const options: IOptionExt[] = pitchers
      .filter((p) => p.isPitcher)
      .map((p) => {
        const o: IOptionExt = {
          label: p.name,
          value: p.playerPk.toString(),
          group: p._mx_details?.teamShortName ?? NO_TEAM_GROUP,
          suffix: <PitcherHandBadge hand={p.pitchHand} />,
        };

        return o;
      })
      // todo: not really sure why but this causes the cardinals group to sort to the top
      .sort((a, b) => {
        if (a.group === b.group) {
          // within a team, sort by player name alphabetically
          return a.label.localeCompare(b.label);
        }

        // put no roster stuff at the end
        if (a.group === NO_TEAM_GROUP) {
          return 1;
        }

        // put no roster stuff at the end
        if (b.group === NO_TEAM_GROUP) {
          return -1;
        }

        // across teams, sort by team names alphabetically
        return (a.group ?? '').localeCompare(b.group ?? '');
      });

    return options;
  }, [pitchers]);

  const pitcher = useMemo(() => {
    if (!pitcherPk) {
      return undefined;
    }

    return pitchers.find((p) => p.playerPk === pitcherPk);
  }, [pitchers, pitcherPk]);

  return (
    <ErrorBoundary componentName="StatcastSearchDialog">
      <CommonDialog
        key={dialog}
        identifier="statcast-search-dialog"
        width={RADIX.DIALOG.WIDTH.SM}
        title="Statcast Search"
        content={
          <Flex direction="column" gap="2">
            <CommonSelectInput
              id="statcast-season"
              placeholder="Select Season"
              disabled={loading}
              value={season?.toString()}
              options={seasonOptions}
              onNumericChange={(v) => setSeason(v)}
              // preserve newest season first
              skipSort
              optional
            />

            <CommonSearchInput
              id="statcast-pitcherPk"
              placeholder="Select Pitcher"
              disabled={loading}
              values={pitcherPk ? [pitcherPk.toString()] : []}
              options={pitcherOptions}
              onChange={(v) => {
                if (v.length === 0) {
                  setPitcherPk(undefined);
                  return;
                }

                setPitcherPk(parseInt(v[0]));
              }}
              skipSort
              optional
            />

            {loading && <Skeleton height="100px" />}

            {!loading && pitcher && (
              <Card size="1" className="bg-background">
                <Flex direction="column" gap="3">
                  {/* pitcher summary */}
                  <Flex gap="2" style={{ width: '100%' }} align="center">
                    <GameDataPlayerAvatar
                      size="2"
                      playerPk={pitcher.playerPk}
                    />
                    <Flex direction="column" flexGrow="1">
                      <Text>{pitcher.name}</Text>

                      <Flex align="center" gap="1">
                        <GameDataTeamLogo
                          isDark={isAppearanceDark()}
                          size="16"
                          teamPK={pitcher._mx_details?.teamPk}
                        />
                        <Text size={RADIX.TEXT.SIZE.HINT}>
                          {pitcher._mx_details?.teamShortName}
                        </Text>
                      </Flex>
                    </Flex>

                    <PitcherHandBadge hand={pitcher.pitchHand} />
                  </Flex>

                  <Grid columns="2" gap="1">
                    {pitchGroups.map((p, i) => (
                      <Flex gap="1" key={i} align="center">
                        {p.invisible ? (
                          <EyeNoneIcon color={RADIX.COLOR.NEUTRAL} />
                        ) : (
                          <Cross2Icon style={{ color: `rgb(${p.rgb})` }} />
                        )}
                        {t(p.typeName)} ({p.type})
                      </Flex>
                    ))}
                  </Grid>
                </Flex>
              </Card>
            )}
          </Flex>
        }
        buttons={[
          {
            label: 'Clear Pitcher',
            disabled: !pitcherPk,
            onClick: () => setPitcherPk(undefined),
          },
          {
            label: 'Done',
            variant: RADIX.BUTTON.VARIANT.PRIMARY,
            onClick: () => setStatsDialog(undefined),
          },
        ]}
        onClose={() => setStatsDialog(undefined)}
      />
    </ErrorBoundary>
  );
};
